import { api } from "@/common/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export type AccountRole = "owner" | "admin" | "operator" | "viewer";
export type AccountingFramework = "pymes_s34" | "ifrs_full";

export type Account = {
  id: string;
  name: string;
  countryCode: string;
  accountingFramework: AccountingFramework;
  // Defaults de depreciación: vienen como string desde Postgres numeric (ratio)
  // y como number (vida útil). Solo presentes en /api/me y respuestas de PATCH.
  defaultUsefulLifeMonths: number;
  defaultResidualValueRatio: string;
  createdAt?: string;
};

// columnas-hato: preferencias de UI de la membresía (usuario × cuenta),
// persistidas en account_memberships.ui_preferences. Shape versionado por
// sección; v1 solo columnas ocultables de la lista del Hato. Llave ausente =
// columna visible. Mirror del tipo UiPreferences del backend (src/db/schema.ts).
export type HatoColumnKey = "name" | "sex" | "lot" | "parities";
export type UiPreferences = {
  hatoColumns?: Partial<Record<HatoColumnKey, boolean>>;
};

export type Membership = {
  id: string;
  accountId: string;
  role: AccountRole;
  status: "active" | "suspended";
  invitedBy: string | null;
  createdAt: string;
  account: Account;
  // Opcional para no romper mocks pre-columnas-hato; el backend lo devuelve
  // siempre ({} si nunca se configuró).
  uiPreferences?: UiPreferences;
};

export type MeResponse = {
  user: {
    id: string;
    email: string;
    firebaseUid: string;
    isActive: boolean;
    displayName: string | null;
  };
  memberships: Membership[];
};

export function useMe() {
  return useQuery<MeResponse>({
    queryKey: ["me"],
    queryFn: () => api.get<MeResponse>("/me"),
  });
}

type CreateAccountInput = {
  name: string;
  countryCode: string;
  accountingFramework: AccountingFramework;
};

type CreateAccountResponse = {
  account: Account;
  membership: Membership;
};

export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation<CreateAccountResponse, Error, CreateAccountInput>({
    mutationFn: (input) => api.post<CreateAccountResponse>("/accounts", input),
    onSuccess: (data) => {
      // Update síncrono del cache de useMe con la nueva membership.
      // Antes hacíamos solo invalidateQueries que dispara refetch ASYNC — el
      // guard de AccountProvider en useCurrentAccount.tsx leía el cache viejo
      // (memberships.length === 0) y redirigía a /onboarding/create-account
      // antes de que el refetch terminara. Resultado: usuario veía la misma
      // página tras crear, asumía que falló, intentaba de nuevo, terminaba con
      // 2 cuentas. Cazado por reality check 2026-05-06.
      qc.setQueryData<MeResponse>(["me"], (old) => {
        if (!old) {
          // Sin cache previo (caso raro) — disparar invalidate como fallback.
          qc.invalidateQueries({ queryKey: ["me"] });
          return old;
        }
        // Defensa: si la membership ya estaba (replay improbable de mutation),
        // no la duplicamos.
        if (old.memberships.some((m) => m.id === data.membership.id)) {
          return old;
        }
        return {
          ...old,
          memberships: [...old.memberships, { ...data.membership, account: data.account }],
        };
      });
      // Invalidate adicional para reconciliar con server state en background.
      // Si el optimistic divergiera del backend (no debería), el refetch corrige.
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

// PATCH /api/accounts/:accountId — owner only (name, country, framework).
export type UpdateAccountMetaInput = {
  name?: string;
  countryCode?: string;
  accountingFramework?: AccountingFramework;
};

export function useUpdateAccountMeta(accountId: string) {
  const qc = useQueryClient();
  return useMutation<{ account: Account }, Error, UpdateAccountMetaInput>({
    mutationFn: (input) => api.patch<{ account: Account }>(`/accounts/${accountId}`, input),
    onSuccess: () => {
      // Invalidar /me para que el switcher refleje el name nuevo.
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

// PATCH /api/accounts/:accountId/me/ui-preferences — cualquier rol activo
// edita las preferencias de SU membresía (columnas-hato). Optimista sobre el
// cache ["me"]: el toggle de columna se refleja al instante (useCurrentAccount
// deriva de ese cache), con rollback + toast si el server rechaza.
export type UpdateUiPreferencesInput = {
  hatoColumns: Partial<Record<HatoColumnKey, boolean>>;
};

export function useUpdateUiPreferences(accountId: string, membershipId: string) {
  const qc = useQueryClient();
  return useMutation<
    { uiPreferences: UiPreferences },
    Error,
    UpdateUiPreferencesInput,
    { previous: MeResponse | undefined }
  >({
    mutationFn: (input) =>
      api.patch<{ uiPreferences: UiPreferences }>(
        `/accounts/${accountId}/me/ui-preferences`,
        input,
      ),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: ["me"] });
      const previous = qc.getQueryData<MeResponse>(["me"]);
      qc.setQueryData<MeResponse>(["me"], (old) => {
        if (!old) return old;
        return {
          ...old,
          memberships: old.memberships.map((m) =>
            m.id === membershipId
              ? {
                  ...m,
                  uiPreferences: {
                    ...m.uiPreferences,
                    hatoColumns: { ...m.uiPreferences?.hatoColumns, ...input.hatoColumns },
                  },
                }
              : m,
          ),
        };
      });
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) {
        qc.setQueryData(["me"], context.previous);
      }
      toast.error("No se pudo guardar la preferencia de columnas");
    },
    onSettled: () => {
      // Reconciliar con server state en background (merge por llave en el
      // backend — si otro dispositivo toggleó otra columna, el refetch la trae).
      void qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

// PATCH /api/accounts/:accountId/defaults — owner+admin (depreciación).
export type UpdateAccountDefaultsInput = {
  defaultUsefulLifeMonths?: number;
  defaultResidualValueRatio?: number;
};

export function useUpdateAccountDefaults(accountId: string) {
  const qc = useQueryClient();
  return useMutation<{ account: Account }, Error, UpdateAccountDefaultsInput>({
    mutationFn: (input) =>
      api.patch<{ account: Account }>(`/accounts/${accountId}/defaults`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}
